import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { CashierTradingAccountMargin } from "@/api/cashier";
import { Summarize } from "@/components/summarize/Summarize";
import { useAccount } from "@/hooks/transfer/useAccount";
import { useAmountForm } from "@/hooks/transfer/useAmountForm";
import { useCurrencyConverter } from "@/hooks/transfer/useCurrencyConverter";
import { TransferExchanger } from "../transfer-exchaner/TransferExchanger";
import { Section } from "@/components/section/Section";
import { useAccountFreeMargin } from "@/hooks/transfer/useAccountFreeMargin";

type EnterAmountProps = {
  tradingAccountList: CashierTradingAccountMargin[];
  tradingAccountToList: CashierTradingAccountMargin[];
  onError: (message: string) => void;
};

const REFETCH_INTERVAL_SECONDS = 20;

export const AccountSelectionWithAmountSection = ({
  tradingAccountList,
  tradingAccountToList,
  onError,
}: EnterAmountProps) => {
  const { t } = useTranslation();

  const { handleAmountChange, error } = useCurrencyConverter();

  const { amountFormData } = useAmountForm();

  const { selectedAccount, selectedToAccount, setSelectedAccount } =
    useAccount();

  const { freeMargin, refetch: refetchAccountFreeMargin } =
    useAccountFreeMargin(selectedAccount?.id || "");

  useEffect(() => {
    if (error) {
      onError(error.message);
    } else {
      onError("");
    }
  }, [error, onError]);

  const exchangeEventHandler = useMemo(
    () => ({
      onProcessingAmountChangeStart: async (amount: number) => {
        return handleAmountChange(amount, true);
      },
      onTradingAmountChangeStart: async (amount: number) => {
        return handleAmountChange(amount, false);
      },
    }),
    [handleAmountChange]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: we should only trigger this when free margin changes or selected account changes
  useEffect(() => {
    if (freeMargin !== undefined && selectedAccount) {
      if (amountFormData.processingValue) {
        exchangeEventHandler.onProcessingAmountChangeStart(
          amountFormData.processingValue
        );
      }
      setSelectedAccount({
        ...selectedAccount,
        margin: freeMargin,
      });
    }
  }, [freeMargin, selectedAccount?.id]);

  return (
    <Section
      title={t("deposit.methods.enter_amount.title")}
      className="overflow-visible"
    >
      <TransferExchanger
        data-testid="transfer-exchanger"
        tradingAccountList={tradingAccountList}
        tradingAccountToList={tradingAccountToList}
        exchangeEventHandlers={exchangeEventHandler}
        error={error}
      />
      <Summarize
        data-testid="transfer-exchange-summarize"
        accountCurrency={selectedToAccount?.currency}
        selectedProcessingCurrency={selectedAccount?.currency}
        tradingValue={amountFormData.processingValue}
        rate={amountFormData.rate}
        showRate={
          !!amountFormData.tradingValue &&
          !!amountFormData.processingValue &&
          selectedAccount?.currency?.code !== selectedToAccount?.currency?.code
        }
        refetchRate={refetchAccountFreeMargin}
        showTotal={false}
        refetchIntervalSeconds={REFETCH_INTERVAL_SECONDS}
        showLastUpdated={
          !!amountFormData.tradingValue && !!amountFormData.processingValue
        }
      />
    </Section>
  );
};
